import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@optimus-web/core';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { OrderItem as GeneratedOrderItem } from '@mews-commander/data-access';
import { getBillItemCount, isBillVirtual } from '@mews-commander/legacy-entities/src/ducks';
import { ApplicationState, OrderItem } from '@mews-commander/types';
import { OptimusProvider } from '@mews-commander/ui';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, lt } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { toggleOrderItemDeselect } from '../ducks/billItems';
import { useChangeItemSuccessPopup } from '../hooks/useChangeItemSuccessPopup';
import { openDeleteBillActionDialog } from '../../../legacy/React/Components/HeadingAction/DeleteBillActionDialog/actions';
import { CancelBillItemDialogForm } from './CancelBillItemDialogForm';

interface Props {
    orderItem: OrderItem;
    billId: string;
    scope: string;
    active: boolean;
    onClose: () => void;
}

interface FormRef {
    submit: () => Promise<void>;
}

const CancelBillItemDialog = ({ orderItem, billId, scope, active, onClose }: Props) => {
    const dispatch = useCommanderThunkDispatch();
    const currentEnterpriseId = useCurrentEnterpriseId();
    const [loading, setLoading] = useState(false);

    const billItemCount = useSelector((state: ApplicationState) =>
        getBillItemCount(state, { billId, enterpriseId: currentEnterpriseId })
    );
    const isVirtualBill = useSelector((state: ApplicationState) => isBillVirtual(state, { id: billId }));
    const isLastItem = billItemCount === 1;
    const isBillDeletable = isLastItem && !isVirtualBill;

    const showChangeItemSuccessPopup = useChangeItemSuccessPopup({
        title: TextKey.ItemRemovedAlertTitle,
        orderItem: orderItem as unknown as GeneratedOrderItem,
    });

    const formRef = useRef<FormRef | null>(null);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await formRef.current?.submit();
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessfulSubmit = () => {
        dispatch(toggleOrderItemDeselect(orderItem.id, scope));

        if (isBillDeletable) {
            dispatch(
                openDeleteBillActionDialog({
                    billId,
                    scope,
                    onClose: showChangeItemSuccessPopup,
                })
            );
        } else {
            showChangeItemSuccessPopup();
        }
    };

    if (!orderItem) return null;

    return (
        <OptimusProvider density="spacious">
            <FloatingPanel
                active={active}
                onClose={onClose}
                title={lt('RemoveItem')}
                size="medium"
                footer={{
                    primary: (
                        <Button variant="primary" onClick={handleSubmit} type="submit">
                            <LocalizedText textKey={TextKey.Remove} />
                        </Button>
                    ),
                    secondary: (
                        <Button variant="tertiary" size="medium" onClick={onClose} type="submit">
                            {lt('Cancel')}
                        </Button>
                    ),
                }}
                loading={loading}
            >
                <CancelBillItemDialogForm
                    handleSuccessfulSubmit={handleSuccessfulSubmit}
                    closeDialog={onClose}
                    orderItem={orderItem}
                    ref={formRef}
                />
            </FloatingPanel>
        </OptimusProvider>
    );
};

export default CancelBillItemDialog;
